import React, { useState, useEffect } from "react";
import {
  Badge,
  Dropdown,
  List,
  Button,
  Space,
  Typography,
  Empty,
  Spin,
  message,
  Popconfirm,
  Tag
} from "antd";
import {
  BellOutlined,
  CheckOutlined,
  DeleteOutlined,
  EyeOutlined,
  CommentOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  TeamOutlined,
  InboxOutlined,
  ShoppingOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import { INotification, NotificationType } from "../../types/notification.type";
import { notificationApi } from "../../api/notificationApi";
import { useNavigate } from "react-router-dom";
import "./notificationBell.component.css";

const { Text } = Typography;

const NotificationBell: React.FC = () => {
  const [notifications, setNotifications] = useState<INotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const [notificationsResponse, unreadResponse] = await Promise.all([
        notificationApi.getNotifications({ limit: 20 }),
        notificationApi.getUnreadCount()
      ]);
      
      const newNotifications = notificationsResponse.notifications || [];
      const newUnreadCount = unreadResponse.count || 0;
      
      // Проверяем, есть ли новые уведомления
      const hasNewNotifications = newUnreadCount > unreadCount;
      
      console.log('Проверка уведомлений:', {
        previousCount: unreadCount,
        newCount: newUnreadCount,
        hasNewNotifications,
        notificationsCount: newNotifications.length
      });
      
      setNotifications(newNotifications);
      setUnreadCount(newUnreadCount);
      
      // Если есть новые уведомления, воспроизводим звук
      if (hasNewNotifications && newUnreadCount > 0) {
        console.log('Обнаружены новые уведомления!');
        
        const latestNotification = newNotifications.find(n => !n.isRead);
        if (latestNotification) {
          console.log('Последнее уведомление:', latestNotification);
          
          console.log('Тип уведомления:', latestNotification.type);
          
          // Воспроизводим звук для всех уведомлений
          await playNotificationSound(0.4, false);
          
          // Показываем браузерное уведомление
          if ('Notification' in window && Notification.permission === 'granted') {
            new Notification('Новое уведомление', {
              body: latestNotification.message,
              icon: '/favicon.ico',
              tag: 'notification',
              requireInteraction: false,
              silent: false
            });
          }
        } else {
          console.log('Не найдено непрочитанных уведомлений');
        }
      } else {
        console.log('Новых уведомлений нет');
      }
    } catch (error) {
      console.error("Ошибка при загрузке уведомлений:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotifications();
    
    // Запрашиваем разрешение на уведомления
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
    
    // Обновляем каждые 30 секунд
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await notificationApi.markAsRead(notificationId);
      setNotifications(prev => 
        prev.map(n => n._id === notificationId ? { ...n, isRead: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      message.error("Ошибка при отметке уведомления");
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationApi.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
      message.success("Все уведомления отмечены как прочитанные");
    } catch (error) {
      message.error("Ошибка при отметке уведомлений");
    }
  };

  const handleDeleteNotification = async (notificationId: string) => {
    try {
      await notificationApi.deleteNotification(notificationId);
      setNotifications(prev => prev.filter(n => n._id !== notificationId));
      if (!notifications.find(n => n._id === notificationId)?.isRead) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      message.success("Уведомление удалено");
    } catch (error) {
      message.error("Ошибка при удалении уведомления");
    }
  };

  const playNotificationSound = async (volume: number = 0.4, isCritical: boolean = false) => {
    try {
      // Проверяем поддержку Audio API
      if (typeof Audio === 'undefined') {
        console.error('Браузер не поддерживает Audio API');
        return;
      }
      
      // Используем один и тот же звук для всех уведомлений
      const soundFile = '/notification-sound.mp3';
      console.log('Воспроизведение звука:', { soundFile, volume, isCritical });
      
      const audio = new Audio(soundFile);
      audio.volume = volume;
      audio.playbackRate = 1.2;
      
      // Добавляем обработчики событий для отладки
      audio.addEventListener('loadstart', () => console.log('Звук начал загружаться'));
      audio.addEventListener('canplay', () => console.log('Звук готов к воспроизведению'));
      audio.addEventListener('play', () => console.log('Звук начал воспроизводиться'));
      audio.addEventListener('ended', () => console.log('Звук завершился'));
      audio.addEventListener('error', (e) => console.error('Ошибка воспроизведения звука:', e));
      
      // Ждем загрузки звука
      await new Promise((resolve, reject) => {
        audio.addEventListener('canplaythrough', resolve);
        audio.addEventListener('error', reject);
        audio.load();
      });
      
      await audio.play();
      console.log('Звук успешно воспроизведен');
    } catch (error) {
      console.error('Звук уведомления недоступен:', error);
    }
  };

  const testNotificationSound = () => {
    playNotificationSound(0.2, false); // Звук для тестирования
  };

  const testCriticalSound = () => {
    playNotificationSound(0.2, true); // Тот же звук для тестирования
  };

  const handleNotificationClick = (notification: INotification) => {
    if (!notification.isRead) {
      handleMarkAsRead(notification._id);
    }
    
    if (notification.taskId) {
      navigate(`/tasks?taskId=${notification.taskId}`);
    }
  };

  const getNotificationIcon = (type: NotificationType) => {
    switch (type) {
      case NotificationType.TaskAssigned:
        return <FileTextOutlined style={{ color: '#1890ff' }} />;
      case NotificationType.TaskCompleted:
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case NotificationType.TaskComment:
        return <CommentOutlined style={{ color: '#722ed1' }} />;
      case NotificationType.TaskOverdue:
        return <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />;
      case NotificationType.TaskDueSoon:
        return <ClockCircleOutlined style={{ color: '#faad14' }} />;
      case NotificationType.TaskStatusChanged:
        return <FileTextOutlined style={{ color: '#13c2c2' }} />;
      case NotificationType.PositionMissingWorkers:
        return <TeamOutlined style={{ color: '#1890ff' }} />;
      case NotificationType.MaterialBelowLimit:
        return <InboxOutlined style={{ color: '#ff4d4f' }} />;
      case NotificationType.MaterialAboveLimit:
        return <InboxOutlined style={{ color: '#faad14' }} />;
      case NotificationType.ProduceBelowLimit:
        return <ShoppingOutlined style={{ color: '#ff4d4f' }} />;
      case NotificationType.ProduceAboveLimit:
        return <ShoppingOutlined style={{ color: '#faad14' }} />;
      default:
        return <BellOutlined />;
    }
  };

  const getNotificationColor = (type: NotificationType) => {
    switch (type) {
      case NotificationType.TaskAssigned:
        return 'blue';
      case NotificationType.TaskCompleted:
        return 'green';
      case NotificationType.TaskComment:
        return 'purple';
      case NotificationType.TaskOverdue:
        return 'red';
      case NotificationType.TaskDueSoon:
        return 'orange';
      case NotificationType.TaskStatusChanged:
        return 'cyan';
      case NotificationType.PositionMissingWorkers:
        return 'blue';
      case NotificationType.MaterialBelowLimit:
        return 'red';
      case NotificationType.MaterialAboveLimit:
        return 'orange';
      case NotificationType.ProduceBelowLimit:
        return 'red';
      case NotificationType.ProduceAboveLimit:
        return 'orange';
      default:
        return 'default';
    }
  };

  const notificationMenu = (
    <div style={{ width: 450, maxHeight: 600, overflow: 'auto' , backgroundColor: '#fff' }}>
      <div style={{ 
        padding: '16px 20px', 
        borderBottom: '2px solid #f0f0f0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: '#fafafa'
      }}>
        <div>
          <Text strong style={{ fontSize: '16px' }}>Уведомления</Text>
          {unreadCount > 0 && (
            <Text type="secondary" style={{ fontSize: '12px', marginLeft: '8px' }}>
              {unreadCount} непрочитанных
            </Text>
          )}
        </div>
        <Space>
          <Button 
            size="small" 
            onClick={testNotificationSound}
            icon={<BellOutlined />}
            type="text"
            title="Тест звука"
            className="sound-test-button"
          />
          <Button 
            size="small" 
            onClick={testCriticalSound}
            icon={<ExclamationCircleOutlined />}
            type="text"
            title="Тест звука (дубликат)"
            style={{ color: '#ff4d4f' }}
            className="critical-sound-button"
          />
          {unreadCount > 0 && (
            <Button 
              size="small" 
              onClick={handleMarkAllAsRead}
              icon={<CheckOutlined />}
              type="primary"
            >
              Отметить все
            </Button>
          )}
        </Space>
      </div>
      
      {loading ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin />
        </div>
      ) : notifications.length === 0 ? (
        <Empty 
          description="Нет уведомлений" 
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{ padding: '20px' }}
        />
      ) : (
        <List
          dataSource={notifications}
          renderItem={(notification) => (
            <List.Item
              style={{
                padding: '16px 20px',
                backgroundColor: notification.isRead ? 'transparent' : '#f6ffed',
                borderBottom: '1px solid #f0f0f0',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              className="notification-item"
              data-type={notification.type}
              onClick={() => handleNotificationClick(notification)}
              actions={[
                !notification.isRead && (
                  <Button
                    type="text"
                    size="small"
                    icon={<CheckOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMarkAsRead(notification._id);
                    }}
                    style={{ color: '#52c41a' }}
                  />
                ),
                <Popconfirm
                  title="Удалить уведомление?"
                  onConfirm={(e) => {
                    e?.stopPropagation();
                    handleDeleteNotification(notification._id);
                  }}
                  okText="Да"
                  cancelText="Нет"
                >
                  <Button
                    type="text"
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={(e) => e.stopPropagation()}
                    style={{ color: '#ff4d4f' }}
                  />
                </Popconfirm>
              ].filter(Boolean)}
            >
              <List.Item.Meta
                avatar={getNotificationIcon(notification.type)}
                title={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Text strong={!notification.isRead} style={{ flex: 1 }}>
                      {notification.title}
                    </Text>
                    <Tag color={getNotificationColor(notification.type)}>
                      {notification.type === NotificationType.PositionMissingWorkers ? 'Должности' :
                       notification.type === NotificationType.MaterialBelowLimit ? 'Материалы (низкий)' :
                       notification.type === NotificationType.MaterialAboveLimit ? 'Материалы (высокий)' :
                       notification.type === NotificationType.ProduceBelowLimit ? 'Продукция (низкая)' :
                       notification.type === NotificationType.ProduceAboveLimit ? 'Продукция (высокая)' :
                       notification.type.replace('_', ' ')}
                    </Tag>
                  </div>
                }
                description={
                  <div>
                    <Text type="secondary" style={{ fontSize: '13px', lineHeight: '1.4' }}>
                      {notification.message}
                    </Text>
                    <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Text type="secondary" style={{ fontSize: '11px' }}>
                        {dayjs(notification.createdAt).format("DD.MM.YYYY HH:mm")}
                      </Text>
                      {notification.taskId && (
                        <Button
                          type="link"
                          size="small"
                          style={{ padding: 0, height: 'auto', fontSize: '11px' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (notification.type === NotificationType.PositionMissingWorkers) {
                              navigate(`/positions`);
                            } else if (notification.type === NotificationType.MaterialBelowLimit || 
                                     notification.type === NotificationType.MaterialAboveLimit) {
                              navigate(`/materials`);
                            } else if (notification.type === NotificationType.ProduceBelowLimit || 
                                     notification.type === NotificationType.ProduceAboveLimit) {
                              navigate(`/produces`);
                            } else {
                              navigate(`/tasks?taskId=${notification.taskId}`);
                            }
                          }}
                        >
                          {notification.type === NotificationType.PositionMissingWorkers ? 'Перейти к должностям' :
                           notification.type === NotificationType.MaterialBelowLimit || 
                           notification.type === NotificationType.MaterialAboveLimit ? 'Перейти к материалам' :
                           notification.type === NotificationType.ProduceBelowLimit || 
                           notification.type === NotificationType.ProduceAboveLimit ? 'Перейти к продукции' :
                           'Перейти к задаче'}
                        </Button>
                      )}
                    </div>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      )}
    </div>
  );

  return (
    <Dropdown
      overlay={notificationMenu}
      trigger={['click']}
      open={visible}
      onOpenChange={setVisible}
      placement="bottomRight"
    >
      <Badge count={unreadCount} size="small">
        <Button
          type="text"
          icon={<BellOutlined />}
          style={{ fontSize: '16px' }}
          className="notification-bell"
          onClick={testNotificationSound}
        />
      </Badge>
    </Dropdown>
  );
};

export default NotificationBell; 